import { useEffect, useMemo, useState } from 'react';

const listeners = new Set();
const timers = new Map();
const removalTimers = new Map();
let queue = [];
let sequence = 0;
const EXIT_DURATION_MS = 220;

const AUTO_CLOSE_BY_TYPE = {
  success: 2400,
  info: 2600,
  warning: 3000,
  error: 3200,
};

const notifyListeners = () => {
  const snapshot = [...queue];
  listeners.forEach((listener) => listener(snapshot));
};

const normalizeMessage = (value) => {
  if (typeof value === 'string') return value;
  if (value && typeof value.message === 'string') return value.message;
  return 'Ocurrio un evento inesperado.';
};

const dismiss = (id) => {
  const item = queue.find((entry) => entry.id === id);
  if (!item) return;
  if (item.leaving) return;

  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }

  queue = queue.map((entry) =>
    entry.id === id ? { ...entry, leaving: true } : entry
  );
  notifyListeners();

  const removalId = setTimeout(() => {
    queue = queue.filter((entry) => entry.id !== id);
    removalTimers.delete(id);
    notifyListeners();
  }, EXIT_DURATION_MS);

  removalTimers.set(id, removalId);
};

const enqueue = (type, message, options = {}) => {
  const id = `${Date.now()}_${sequence++}`;
  const autoClose = Number.isFinite(options?.autoClose)
    ? Number(options.autoClose)
    : options?.autoClose === false
      ? 0
      : AUTO_CLOSE_BY_TYPE[type] || 2600;

  queue = [
    ...queue,
    {
      id,
      type,
      message: normalizeMessage(message),
      leaving: false,
    },
  ];
  notifyListeners();

  if (autoClose > 0) {
    const timeoutId = setTimeout(() => dismiss(id), autoClose);
    timers.set(id, timeoutId);
  }

  return id;
};

const subscribe = (listener) => {
  listeners.add(listener);
  listener([...queue]);
  return () => listeners.delete(listener);
};

const TYPE_STYLES = {
  success: {
    accent: 'bg-emerald-500',
    icon: '✓',
    iconBg: 'bg-emerald-100 text-emerald-700',
  },
  error: {
    accent: 'bg-red-500',
    icon: '!',
    iconBg: 'bg-red-100 text-red-700',
  },
  info: {
    accent: 'bg-peabodyBlue',
    icon: 'i',
    iconBg: 'bg-peabodyLight text-peabodyDark',
  },
  warning: {
    accent: 'bg-amber-500',
    icon: '!',
    iconBg: 'bg-amber-100 text-amber-700',
  },
};

export const toast = {
  success: (message, options) => enqueue('success', message, options),
  error: (message, options) => enqueue('error', message, options),
  info: (message, options) => enqueue('info', message, options),
  warning: (message, options) => enqueue('warning', message, options),
  warn: (message, options) => enqueue('warning', message, options),
  dismiss,
};

export const ToastContainer = () => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => subscribe(setNotifications), []);

  const current = notifications[0] || null;

  const currentStyle = useMemo(() => {
    if (!current) return TYPE_STYLES.info;
    return TYPE_STYLES[current.type] || TYPE_STYLES.info;
  }, [current]);

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[14000] flex items-start justify-center pt-6 px-4 pointer-events-none">
      <div className="w-full max-w-md pointer-events-auto">
        <div className="relative overflow-hidden rounded-2xl border border-white/70 bg-white/95 shadow-[0_20px_50px_rgba(0,0,0,0.18)] backdrop-blur-md">
          <div
            className={`h-1.5 w-full ${currentStyle.accent}`}
            style={{
              animation: current.leaving
                ? 'appToastOut 220ms ease-in forwards'
                : 'appToastIn 260ms ease-out both',
            }}
          />
          <div
            className="p-4 sm:p-5"
            style={{
              animation: current.leaving
                ? 'appToastOut 220ms ease-in forwards'
                : 'appToastIn 260ms ease-out both',
            }}
          >
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStyle.iconBg}`}>
                {currentStyle.icon}
              </div>
              <div className="flex-1 pr-6">
                <p className="text-sm font-semibold text-peabodyDark leading-relaxed">
                  {current.message}
                </p>
              </div>
              <button
                type="button"
                className="absolute top-3 right-3 text-gray-400 hover:text-peabodyDark transition-colors"
                onClick={() => dismiss(current.id)}
                aria-label="Cerrar notificacion"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
